import { FC, useState, useMemo } from 'react';
import { Button } from 'react-daisyui';
import DatePickerInput from '@/components/functional/DatePickerInput/DatePickerInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';

type IntroCallRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: IntroCallRequestData) => void;
  carerName: string;
  isLoading?: boolean;
  availabilityDates?: { startDate: string; endDate: string };
};

export type IntroCallRequestData = {
  preferred_date?: string;
  preferred_time_from?: string;
  preferred_time_to?: string;
  message?: string;
  meeting_link?: string;
};

const IntroCallRequestModal: FC<IntroCallRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  carerName: _carerName,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<IntroCallRequestData>({
    preferred_date: '',
    preferred_time_from: '09:00',
    preferred_time_to: '10:00',
    message: '',
    meeting_link: '',
  });

  const [errors, setErrors] = useState({
    preferred_date: '',
    meeting_link: '',
  });

  // Generate time options (00:00 - 23:30, every 30 minutes)
  // Must be before the early return to maintain hook order
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({
          value: timeValue,
          label: timeValue,
        });
      }
    }
    return options;
  }, []);

  // Filter end time options to only show times after preferred_time_from
  const endTimeOptions = useMemo(() => {
    if (!formData.preferred_time_from) return timeOptions;
    
    return timeOptions.filter(option => {
      return option.value > formData.preferred_time_from!;
    });
  }, [timeOptions, formData.preferred_time_from]);

  // Disable past dates only - intro calls can be booked on any future date
  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors = {
      preferred_date: '',
      meeting_link: '',
    };

    if (!formData.preferred_date) {
      newErrors.preferred_date = 'Date is required';
    }

    if (!formData.meeting_link) {
      newErrors.meeting_link = 'Meeting link is required';
    }

    setErrors(newErrors);

    // If there are errors, don't submit
    if (newErrors.preferred_date || newErrors.meeting_link) {
      return;
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="w-full p-5 sm:p-8">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold pr-4">Select preferred days and times</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none flex-shrink-0"
              disabled={isLoading}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <DatePickerInput
                  value={formData.preferred_date || ''}
                  onChange={(value) => {
                    setFormData({ ...formData, preferred_date: value });
                    setErrors({ ...errors, preferred_date: '' });
                  }}
                  placeholder="dd/mm/yyyy"
                  disabled={isLoading}
                  displayFormat="DD/MM/YYYY"
                  disabledDates={isDateDisabled}
                />
                {errors.preferred_date && (
                  <p className="text-red-500 text-xs mt-1">{errors.preferred_date}</p>
                )}
              </div>

              <div className="mt-2">
                {/* Mobile: stacked */}
                <div className="flex flex-col gap-2 sm:hidden">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-6 shrink-0">From</span>
                    <SelectInput
                      value={formData.preferred_time_from || '09:00'}
                      onChange={(value) => {
                        const newFormData = { ...formData, preferred_time_from: value };
                        if (formData.preferred_time_to && formData.preferred_time_to <= value) {
                          const nextTimeSlot = timeOptions.find(opt => opt.value > value);
                          if (nextTimeSlot) newFormData.preferred_time_to = nextTimeSlot.value;
                        }
                        setFormData(newFormData);
                      }}
                      options={timeOptions}
                      disabled={isLoading}
                      wrapperClassName="flex-1"
                      placeholder="00:00"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-6 shrink-0">To</span>
                    <SelectInput
                      value={formData.preferred_time_to || '10:00'}
                      onChange={(value) => setFormData({ ...formData, preferred_time_to: value })}
                      options={endTimeOptions}
                      disabled={isLoading}
                      wrapperClassName="flex-1"
                      placeholder="00:00"
                    />
                  </div>
                </div>
                {/* Desktop: side by side */}
                <div className="hidden sm:flex gap-3 items-center">
                  <SelectInput
                    value={formData.preferred_time_from || '09:00'}
                    onChange={(value) => {
                      const newFormData = { ...formData, preferred_time_from: value };
                      if (formData.preferred_time_to && formData.preferred_time_to <= value) {
                        const nextTimeSlot = timeOptions.find(opt => opt.value > value);
                        if (nextTimeSlot) newFormData.preferred_time_to = nextTimeSlot.value;
                      }
                      setFormData(newFormData);
                    }}
                    options={timeOptions}
                    disabled={isLoading}
                    wrapperClassName="flex-1"
                    placeholder="00:00"
                  />
                  <span className="text-gray-500">to</span>
                  <SelectInput
                    value={formData.preferred_time_to || '10:00'}
                    onChange={(value) => setFormData({ ...formData, preferred_time_to: value })}
                    options={endTimeOptions}
                    disabled={isLoading}
                    wrapperClassName="flex-1"
                    placeholder="00:00"
                  />
                </div>
              </div>
            </div>

            <TextArea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Add brief free-text notes (optional)"
              rows={4}
              disabled={isLoading}
            />

            <div>
              <TextInput
                value={formData.meeting_link}
                onChange={(e) => {
                  setFormData({ ...formData, meeting_link: e.target.value });
                  setErrors({ ...errors, meeting_link: '' });
                }}
                placeholder="Enter the link"
                disabled={isLoading}
              />
              {errors.meeting_link ? (
                <p className="text-red-500 text-xs mt-1">{errors.meeting_link}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Zoom or phone link for intro call (required)</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-black text-white border-none hover:opacity-90 rounded-lg py-3"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IntroCallRequestModal;
